import qs from 'qs'
import get from 'lodash/get'
import camelCaseKeysDeep from 'camelcase-keys-deep'

import getConstants from '../../constants'
import {
  isPostalCode,
  getCountryLanguage,
  getSupportedEUCountry,
} from '../../utils'
import { FilterParams, StoreFiltersState } from './typings'
import { ALL_REGIONS_VALUE, filterMap } from './constants'
import { queryGeocodeApi } from './utils'
import { reqError, isReqError } from '../Request/error'
import { getLanguageArg } from 'helpers/getLanguageArg'

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || ''

const REFERRER = 'storelocator'
const CONSTANTS = getConstants()
const { AREAS, CODE: REGION_CODE, DISPLAY_NAME: REGION_NAME, IS_EU } = CONSTANTS

export type GetStoreListProps = {
  baseUrl?: string
  country: string
  language: string
  limit?: number
  shouldReset?: boolean
  // Geo-coordinates search
  latitude?: number
  longitude?: number
  maxDistance?: number
  // Keyword search
  keyword?: string
  areaCode?: string
  filters: StoreFiltersState
  offset: number
  unitForDistance: string
  states?: AreasType
}

export type GetAllStoreListProps = {
  country: string
  limit: number
  areaCode?: string
}

const getFilterQueryString = (
  filters: StoreFiltersState,
  filterParam: FilterParams,
) => {
  const filterString = Object.entries(filters)
    .filter(
      ([key, value]) =>
        get(filterMap[key], 'filterParam') === filterParam && value,
    )
    .map(([key]) => filterMap[key].code)
    .join(',')

  if (filterString) {
    return filterString
  }
}

type FilterQueryParams = {
  [FilterParams.storeItem]: string | undefined
  [FilterParams.storeType]: string | undefined
  [FilterParams.accessibility]: string | undefined
  [FilterParams.storeService]: string | undefined
}

const mapFiltersToQueryParams = (
  filters: StoreFiltersState,
): FilterQueryParams => {
  return {
    [FilterParams.storeItem]: getFilterQueryString(
      filters,
      FilterParams.storeItem,
    ),
    [FilterParams.storeType]: get(filterMap[filters.storeSize], 'code'),
    [FilterParams.accessibility]: getFilterQueryString(
      filters,
      FilterParams.accessibility,
    ),
    [FilterParams.storeService]: getFilterQueryString(
      filters,
      FilterParams.storeService,
    ),
  }
}

const getSearchParams = ({
  keyword,
  areaCode,
  latitude,
  longitude,
  maxDistance,
  unitForDistance,
}: Partial<GetStoreListProps>) => {
  if (keyword || (!IS_EU && areaCode)) {
    return {
      keyword,
      ...(IS_EU || ALL_REGIONS_VALUE == areaCode
        ? {}
        : { stateCode: areaCode }),
    }
  }

  if (latitude && longitude) {
    return {
      latitude: latitude,
      longitude: longitude,
      distance: maxDistance,
      unitForDistance: unitForDistance,
    }
  }

  return {}
}

const getRequestCountryAndLanguage = (
  countryCode: string,
  areaCode?: string,
) => {
  if (IS_EU) {
    if (areaCode) {
      return [areaCode, getCountryLanguage(areaCode)]
    } else {
      const supportedEUCountry = getSupportedEUCountry(countryCode)
      if (supportedEUCountry !== countryCode) {
        return [supportedEUCountry, getCountryLanguage(supportedEUCountry)]
      }
    }
  }

  return [countryCode, CONSTANTS.LANGUAGE]
}

export const getAllStoreList = async ({
  country,
  limit,
  areaCode,
}: GetAllStoreListProps) => {
  const [countryCode, lang] = getRequestCountryAndLanguage(country, areaCode)
  const query = qs.stringify({
    limit,
    RESET: true,
    lang: getLanguageArg(lang),
    offset: 0,
    r: REFERRER,
  })
  const response = await fetch(
    `${API_BASE_URL}/${countryCode}/api/storelocator/v1/${lang}/stores?${query}`,
    //{ headers: { 'x-fr-clientID': CONSTANTS.CLIENT_ID[CONSTANTS.BRAND] } },
  )
  const { status: statusCode } = response
  const json = await response.json()

  if (isReqError(statusCode)) {
    throw reqError(statusCode, json.message)
  }
  return camelCaseKeysDeep(json)
}

export const getStoreList = async ({
  baseUrl = API_BASE_URL,
  country,
  limit,
  latitude,
  longitude,
  keyword,
  offset,
  maxDistance = CONSTANTS.MAX_DISTANCE,
  shouldReset = true,
  filters,
  areaCode,
  unitForDistance,
  states = AREAS,
}: GetStoreListProps) => {
  const postalCodeCoordsOrKeyword = await searchForMatchingPostalCode(
    keyword,
    areaCode,
    states,
  )

  const searchParams = getSearchParams({
    areaCode,
    latitude,
    longitude,
    maxDistance,
    unitForDistance,
    ...postalCodeCoordsOrKeyword,
  })

  const filterParams = mapFiltersToQueryParams(filters)
  const [countryCode, lang] = getRequestCountryAndLanguage(country, areaCode)
  const query = qs.stringify({
    limit,
    offset,
    lang: getLanguageArg(lang),
    RESET: shouldReset,
    r: REFERRER,
    ...filterParams,
    ...searchParams,
  })

  const response = await fetch(
    `${API_BASE_URL}/${countryCode}/api/storelocator/v1/${lang}/stores?${query}`,
    //{ headers: { 'x-fr-clientID': CONSTANTS.CLIENT_ID[CONSTANTS.BRAND] } },
  )
  const { status: statusCode } = response
  const json = await response.json()

  if (isReqError(statusCode)) {
    throw reqError(statusCode, json.message)
  }

  return camelCaseKeysDeep(json)
}

export type GetStoreDetailsProps = {
  baseUrl?: string
  country: string
  storeId: number | string
  areaCode?: string
}

export const getStoreDetails = async ({
  baseUrl = API_BASE_URL,
  country,
  storeId,
  areaCode,
}: GetStoreDetailsProps) => {
  const [countryCode, lang] = getRequestCountryAndLanguage(country, areaCode)
  const query = qs.stringify({
    lang: getLanguageArg(lang),
    store_id: storeId,
    r: REFERRER,
  })
  const url = `${API_BASE_URL}/${countryCode}/api/storelocator/v1/${lang}/stores/${storeId}`
  const response = await fetch(url, {
    //headers: { 'x-fr-clientID': CONSTANTS.CLIENT_ID[CONSTANTS.BRAND] },
  })
  const { status: statusCode } = response
  const json = await response.json()

  if (isReqError(statusCode)) {
    throw reqError(statusCode, json.message, json.code)
  }

  const storeInfo: Components.Schemas.StoreDetail = json.result

  const lon = parseFloat(storeInfo.longitude!)
  const lat = parseFloat(storeInfo.latitude!)

  if (lat > 90 || lat < -90) {
    const errorMsg = `getStoreDetail: Invalid Latitude from ${url}: ${lat}`
    // eslint-disable-next-line no-console
    console.error(errorMsg)
    throw reqError(statusCode, errorMsg)
  }

  if (lon > 180 || lon < -180) {
    const errorMsg = `getStoreDetail: Invalid Longitude ${url}: ${lon}`
    // eslint-disable-next-line no-console
    console.error(errorMsg)
    throw reqError(statusCode, errorMsg)
  }

  const res: any = {}
  res.entities = {}
  res.entities[storeId] = json.result
  res.result = json
  return camelCaseKeysDeep(res)
}

export const getGeoCode = async (
  postcode: string,
  areaCode?: string,
  states?: AreasType,
) => {
  const address =
    areaCode && states
      ? `${postcode} ${states[areaCode]} ${REGION_NAME}`
      : `${postcode} ${REGION_NAME}`

  try {
    const results = await queryGeocodeApi({
      address,
      componentRestrictions: {
        postalCode: postcode,
        country: REGION_CODE,
      },
      region: REGION_CODE,
    })
    return results
  } catch (error) {
    return
  }
}

const searchForMatchingPostalCode = async (
  keyword?: string,
  areaCode?: string,
  states?: AreasType,
) => {
  if (keyword && isPostalCode(keyword)) {
    const results = await getGeoCode(keyword, areaCode, states)

    if (results && results.length) {
      const { lat, lng } = results[0].geometry.location

      return {
        latitude: lat(),
        longitude: lng(),
      }
    }
  }

  return { keyword }
}
